import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { addDaysYmd, fmtInZone, ymdInZone, zonedToUtc } from './schedule';

export interface PeriodPick {
  windowStart: string;
  windowEnd: string;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Boundaries are midnights on this zone's wall clock. */
  timezone: string;
  onConfirm: (period: PeriodPick) => void | Promise<unknown>;
  loading?: boolean;
}

type Quick = { key: string; label: string; range: (today: string) => { from: string; to: string } };

const QUICK: Quick[] = [
  { key: 'yesterday', label: 'Yesterday', range: (t) => ({ from: addDaysYmd(t, -1), to: addDaysYmd(t, -1) }) },
  { key: 'week', label: 'Last 7 days', range: (t) => ({ from: addDaysYmd(t, -7), to: addDaysYmd(t, -1) }) },
  { key: 'last_week', label: 'Last week (Mon–Sun)', range: (t) => {
      const dow = new Date(`${t}T00:00:00Z`).getUTCDay();
      const monday = addDaysYmd(t, -((dow + 6) % 7));
      return { from: addDaysYmd(monday, -7), to: addDaysYmd(monday, -1) };
    } },
  { key: 'month', label: 'Last 30 days', range: (t) => ({ from: addDaysYmd(t, -30), to: addDaysYmd(t, -1) }) },
  { key: 'last_month', label: 'Last month', range: (t) => {
      const [y, m] = t.split('-').map(Number) as [number, number];
      const first = `${m === 1 ? y - 1 : y}-${String(m === 1 ? 12 : m - 1).padStart(2, '0')}-01`;
      return { from: first, to: addDaysYmd(`${y}-${String(m).padStart(2, '0')}-01`, -1) };
    } },
  { key: 'mtd', label: 'This month so far', range: (t) => ({ from: `${t.slice(0, 7)}-01`, to: t }) },
  { key: 'quarter', label: 'Last 90 days', range: (t) => ({ from: addDaysYmd(t, -90), to: addDaysYmd(t, -1) }) },
];

/** Pick a period for a one-off run or preview: quick ranges or explicit dates, on the report's own time zone. */
export function RangeDialog({ open, onClose, title, description, confirmLabel, timezone, onConfirm, loading }: Props) {
  const today = useMemo(() => ymdInZone(new Date(), timezone), [timezone]);
  const [quick, setQuick] = useState<string>('week');
  const [from, setFrom] = useState(() => QUICK[1]!.range(today).from);
  const [to, setTo] = useState(() => QUICK[1]!.range(today).to);

  const pickQuick = (q: Quick) => {
    const r = q.range(today);
    setQuick(q.key);
    setFrom(r.from);
    setTo(r.to);
  };
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to && to <= today;
  const start = valid ? zonedToUtc(timezone, from) : null;
  const endRaw = valid ? zonedToUtc(timezone, addDaysYmd(to, 1)) : null;
  const end = endRaw && endRaw.getTime() > Date.now() ? new Date() : endRaw;
  const days = start && end ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000)) : 0;
  const tooLong = days > 366;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Quick ranges">
            {QUICK.map((q) => (
              <button key={q.key} type="button" aria-pressed={quick === q.key} onClick={() => pickQuick(q)} className={cn('border px-2.5 py-1 text-xs transition-colors', quick === q.key ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent')}>
                {q.label}
              </button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="From" htmlFor="rg-from">
              <Input id="rg-from" type="date" value={from} max={today} onChange={(e) => { setFrom(e.target.value); setQuick(''); }} />
            </Field>
            <Field label="To" htmlFor="rg-to" description="Inclusive; today ends at the current time.">
              <Input id="rg-to" type="date" value={to} max={today} onChange={(e) => { setTo(e.target.value); setQuick(''); }} />
            </Field>
          </div>
          <p className={cn('text-xs', valid && !tooLong ? 'text-muted-foreground' : 'text-destructive')}>
            {!valid ? 'Choose a start on or before the end, and no later than today.' : tooLong ? 'A period can cover at most a year.' : start && end ? `${fmtInZone(start.toISOString(), timezone, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} → ${fmtInZone(end.toISOString(), timezone, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} · ${days} ${days === 1 ? 'day' : 'days'} · ${timezone}` : ''}
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!valid || tooLong || loading}
            onClick={() => {
              if (!start || !end) return;
              const label = from === to ? fmtInZone(start.toISOString(), timezone, { month: 'short', day: 'numeric', year: 'numeric' }) : `${fmtInZone(start.toISOString(), timezone, { month: 'short', day: 'numeric' })} – ${fmtInZone(new Date(end.getTime() - 1).toISOString(), timezone, { month: 'short', day: 'numeric', year: 'numeric' })}`;
              void onConfirm({ windowStart: start.toISOString(), windowEnd: end.toISOString(), label });
            }}
          >
            {loading ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
